import createClient from "openapi-fetch";
import type { JwtPayload } from "jwt-decode";
import { jwtDecode } from "jwt-decode";
import type { Messaging } from "firebase/messaging";
import { getInstallations, getId, deleteInstallations } from "firebase/installations";
import { addDays, addYears, format, isValid, parse, startOfDay } from "date-fns";
import type { Role, UserInfo, AuthToken, AccessTokenWithLoginOption, AuthTokenState } from "~/types/entity/user";
import { displayRoleList, validRoleList } from "~/types/entity/user";
import type { MainViewMode } from "~/types/entity/cWebCatalog";
import * as cWebCatalogApi from "~/utils/cWebCatalogApi";
import { CACHE_BUST_HEADER, cachingFetch } from "~/utils/requestCache";
import { isAuthorizedRole, isTimeboxedMode, useEntraId2Token } from "~/utils/urlHelper";
import { useTrialUplift } from "~/composables/useTrialUplift";
import type { components, paths } from "~/types/entity/emerIntwcApiWeb.schema";
import { usePushState } from "~/composables/state/usePushState";
import useCirclemsConfig from "~/composables/config/useCirclemsConfig";
import { usePromotePaymentDialogState } from "~/composables/state/usePromotePaymentDialogState";
import { useUserState } from "~/composables/state/useUserState";
import { useMsal } from "~/composables/useMsal";
import type { PushActivity } from "~/types/entity/push";


import { useEasyAuthRedirect } from "~/composables/useEasyAuthRedirect";

/**
 * 設定可能な最大年齢
 */
const maxBirthAge = 125;

/** 年齢・生年月日入力の基準タイムゾーン（国際版も含め JST） */
export const birthDateTimeZone = "Asia/Tokyo";

/**
 * yyyy-MM-dd をカレンダー日付としてパースする。
 * `new Date("yyyy-MM-dd")` / parseISO の UTC midnight 解釈による TZ ずれを避ける。
 */
export const parseBirthDateString = (value: string): Date | undefined => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const parsed = parse(value, "yyyy-MM-dd", new Date(2000, 0, 1));
  if (!isValid(parsed) || format(parsed, "yyyy-MM-dd") !== value) return undefined;
  return startOfDay(parsed);
};

/**
 * 現在の JST カレンダー日付（ローカル Date の年月日として保持）。
 * API の now.ToJst().Date と年齢境界を揃える。
 */
export const getTodayJst = (): Date => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: birthDateTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = Number(parts.find(p => p.type === "year")?.value);
  const month = Number(parts.find(p => p.type === "month")?.value);
  const day = Number(parts.find(p => p.type === "day")?.value);
  return new Date(year, month - 1, day);
};

const toBirthDateOnly = (birthDate: Date | string | undefined): Date | undefined => {
  if (birthDate === undefined) return undefined;
  if (typeof birthDate === "string") return parseBirthDateString(birthDate);
  if (Number.isNaN(birthDate.getTime())) return undefined;
  return startOfDay(birthDate);
};

/**
 * .NET DateOnly.AddYears 相当 (date-fns addYears。2月29日の非うるう年は 2月28日)。
 * GetCirclemsR18 / IsBirthDateInAllowedAgeRange と揃える。
 */
export const addYearsDateOnly = (date: Date, years: number) => addYears(startOfDay(date), years);

/**
 * 生年月日入力の下限 (yyyy-MM-dd)。126歳到達日の翌日 (= 年齢 maxBirthAge の最古日)。
 * 基準日は JST。AddYears はうるう日で可逆でないため、範囲外なら翌日へ繰り上げる。
 */
export const getMinBirthDate = () => {
  const today = getTodayJst();
  let min = addDays(addYearsDateOnly(today, -(maxBirthAge + 1)), 1);
  // 例: today=非うるう年2/28 のとき候補が 2/29 になり、AddYears(126) が today に届いて無効になる
  while (addYearsDateOnly(min, maxBirthAge + 1).getTime() <= today.getTime()) {
    min = addDays(min, 1);
  }
  return format(min, "yyyy-MM-dd");
};

/**
 * 生年月日入力の上限 (yyyy-MM-dd)。JST の今日
 */
export const getMaxBirthDate = () => format(getTodayJst(), "yyyy-MM-dd");

/**
 * 生年月日から年齢を計算する (.NET DateOnly.AddYears / date-fns addYears と同じ加齢日)。
 * 基準日は JST。differenceInYears は 2/29 生まれの非うるう年 2/28 で加齢しないため使わない。
 */
export const getAgeFromBirthDate = (birthDate: Date | string | undefined) => {
  if (birthDate === undefined) return 0;
  const birth = toBirthDateOnly(birthDate);
  if (birth === undefined) return Number.NaN;
  const today = getTodayJst();
  let age = today.getFullYear() - birth.getFullYear();
  if (addYearsDateOnly(birth, age).getTime() > today.getTime()) {
    age--;
  }
  return age;
};

const isAgeOver18 = (birthdate: Date | string | undefined) => getAgeFromBirthDate(birthdate) >= 18;

/**
 * 既存の生年月日を画面に反映可能か (0歳以上 maxBirthAge 以下。NaN は除外)
 */
export const isBirthDateInEditableAgeRange = (birthDate: Date | string | undefined) => {
  const age = getAgeFromBirthDate(birthDate);
  return 0 <= age && age <= maxBirthAge;
};

/**
 * 生年月日が入力可能・適正な範囲か (0歳以上かつ maxBirthAge 以下)
 */
export const isValidBirthDate = (birthDate: Date | string | undefined) => {
  if (birthDate === undefined) return false;
  const age = getAgeFromBirthDate(birthDate);
  return 0 <= age && age <= maxBirthAge;
};

export type IUserSnsAuth = components["schemas"]["UserSnsAuth"];

export type UpdateUserInfoOptions = {
  /**
   * クライアント側キャッシュ無効化用の時刻 (ミリ秒。Date.now())。
   * 指定時はその値を内部ヘッダ X-Cache-Bust-T に載せ、キャッシュキーを変えて強制再取得する。
   * 省略時は通常 (キャッシュ/dedup 有効)。URL/クエリには付与しない (Documents/Plan/AuthCache)。
   */
  t?: number
};

export const useUserModel = () => {
  const state = useUserState();
  const pushState = usePushState();
  // EasyAuth (App Service 認証) 再認証誘導の状態機械 (Cookie2Token の 302 対応)。
  const easyAuthRedirect = useEasyAuthRedirect();

  const userInfo = () => state.get().value.userInfo;
  const isLoginAttempted = () => (state.get().value.token?.loginAttemptAt ?? 0) > 0;
  const isLogin = () => (state.get().value.token?.accessToken?.length ?? 0) > 0;
  const isAuthorization = () => (state.get().value.userInfo?.roles?.length ?? 0) > 0;
  const canDisplayR18 = () => state.get().value.userInfo?.circlems_r18 === 1 && isAgeOver18(state.get().value.userInfo?.birthdate);
  /**
   * 課金前ユーザー情報入力が必要か（生年月日未設定または 0～maxBirthAge 外）。
   * 成人コンテンツ表示フラグ (circlems_r18) は問わない（JP版。画像表示は canDisplayR18 を使用）。
   */
  const needsUserInfoForPayment = () => !isValidBirthDate(userInfo().birthdate);
  const displayStatus = () => state.get().value.userInfo?.roles?.find(role => displayRoleList.includes(<Role /* 絞り込み確認のためだけなので cast してよい */>role));
  const clearUserState = () => state.set({ userInfo: convertToUserInfo(undefined), token: undefined });
  const setUserState = (token: AuthToken | undefined, userInfoItem: components["schemas"]["V2UserInfoItem"] | undefined) =>
    state.set({
      token: token,
      userInfo: convertToUserInfo(userInfoItem),
    });
  const isPush = () => pushState.get().value.push.enabled;
  const getAuthToken: () => AuthToken | undefined = () => state.get().value.token;
  const getPushState = () => pushState.get().value;

  /**
   * トークン有効期限内であればトークンを返す。そうでなければ undefined を返す
   */
  const getValidAuthToken: () => AuthToken | undefined = () =>
    (state.get().value.token?.expiresAt ?? 0) > Date.now() / 1000
      ? state.get().value.token
      : undefined;

  // ここからトークン取得用 getAccessTokenWithLogin のためのコード
  const xWwwFormUrlencode = (body: unknown) => {
    const urlEncodedData = new URLSearchParams();
    const bodyObject = body as Record<string, unknown>; // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
    for (const [key, value] of Object.entries(bodyObject)) {
      urlEncodedData.append(key, value as string);
    }
    return urlEncodedData.toString();
  };

  // cacheBustT 指定時に POST へ付与する内部ヘッダ。requestCache がキャッシュキーに含めて無効化する
  const cacheBustHeaders = (cacheBustT?: number): Record<string, string> =>
    cacheBustT === undefined ? {} : { [CACHE_BUST_HEADER]: String(cacheBustT) };

  const getAccessTokenWithLogin = async (cacheBustT?: number): Promise<string | undefined> => {
    // オフライン時に例外が投げられるが認証できなかったものとして扱う
    try {
      return useEntraId2Token
        ? await getAccessTokenWithLoginEntraId2Token(cacheBustT)
        : await getAccessTokenWithLoginCookie2Token(cacheBustT);
    }
    catch {
      return undefined;
    }
  };

  const msal = useMsal();
  const getAccessTokenWithLoginEntraId2Token = async (cacheBustT?: number): Promise<string | undefined> => {
    {
      // 有効なトークンが userState にあればそれを採用する
      const accessToken = getValidAuthToken()?.accessToken;
      if (accessToken) {
        return accessToken;
      }
    }

    const loginAttemptAt: number = Date.now();
    const authenticationResult = await msal.getActiveAccount();
    /**
     * 認証できなかった時の後処理
     */
    const notAuthenticated = async (authTokenState: AuthTokenState, error?: string) => {
      setUserState({
        accessToken: "",
        expiresAt: undefined,
        iat: undefined,
        loginAttemptAt: loginAttemptAt, // ログインを試みたことは記録したい
        authTokenState: authTokenState,
        error: error,
      }, undefined);
      // EntraID 的にログインできたが トークンが取得できなかった場合に ログアウトして別のユーザーになるよう遷移する処理が以前含まれていたが
      // この処理は msal.vue ページ側で認証状態判定して制御するべき。そのための 状態を State に保存した
      // if (authenticationResult?.idToken) {
      //   await msal.logoutRedirect();
      // }
      return undefined;
    };
    if (!authenticationResult?.idToken) {
      return await notAuthenticated("NoIdToken", "no idToken in ActiveAccount");
    }

    // id_token から このアプリケーション用のアクセストークンの取得
    const client = createClient<paths>({
      fetch: cachingFetch, // 認証系 POST のクライアント側キャッシュ (Documents/Plan/AuthCache)
      headers: {
        "X-EntraID-IdToken": authenticationResult.idToken,
        // RFC 6749 OAuth2 における Request は x-www-form-urlencoded
        "Content-Type": "application/x-www-form-urlencoded",
      },
      bodySerializer: xWwwFormUrlencode,
    });
    const response = await client.POST("/api/v2/EntraId2Token", {
      headers: cacheBustHeaders(cacheBustT),
      body: {
        scope: "openid",
        grant_type: "client_credentials",
      }
    });
    const data = response.data;
    if (data?.id_token && data?.access_token) {
      const accessToken: string = data.access_token;
      // この /api/v2/UserInfo 呼び出しはトークンの検証機能を兼ねているため setStateAuthToken よりも前に実施する
      const responseUserInfo = await client.POST("/api/v2/UserInfo", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          ...cacheBustHeaders(cacheBustT),
        }
      });
      // ページから /api/v2/UserInfo を呼ぶ場合については src/composables/view-model/user.ts に setStateUserInfo を記述している
      const userInfoItem = responseUserInfo?.data;
      // UserInfo の取得はトークンの検証を兼ねている。取得できなければ未ログインとして扱う
      // (これにより各ページの checkPermission が undefined を受け取り /auth/login へ遷移する)
      if (!userInfoItem) {
        return await notAuthenticated("FailEntraId2Token", "failed to fetch UserInfo");
      }
      const jwt = jwtDecode<JwtPayload & { entitlements?: string }>(data.id_token);
      setUserState({
        ...jwt,
        accessToken: accessToken,
        expiresAt: jwt.exp ?? undefined,
        iat: jwt.iat ?? undefined,
        entitlements: jwt.entitlements ?? undefined,
        loginAttemptAt: loginAttemptAt,
        authTokenState: "Authenticated",
      }, userInfoItem);
      return accessToken;
    }
    // 404 NoActiveUser: EntraID トークンは有効だが AccountStatus == 有効 のユーザーが存在しない (退会/凍結 など)
    if (response.response?.status === 404) {
      return await notAuthenticated("NoActiveUser", "NoActiveUser");
    }
    return await notAuthenticated("FailEntraId2Token", response.error);
  };

  const getAccessTokenWithLoginCookie2Token = async (cacheBustT?: number): Promise<string | undefined> => {
    // const { getValidAuthToken, setUserState } = fetchoption;
    {
    // 有効なトークンが userState にあればそれを採用する
      const accessToken = getValidAuthToken()?.accessToken;
      if (accessToken) {
        return accessToken;
      }
    }

    // EasyAuth 戻りガードを消費する (成功/失敗を問わず開始時に必ず解除するため残留しない)。
    // 再認証から戻った直後にまた 302 ならループ判定は handleEasyAuthRedirect が担う。
    easyAuthRedirect.beginAttempt();

    // アクセストークンの取得
    const loginAttemptAt: number = Date.now();
    const cookie = useRequestHeader("cookie");
    const client = createClient<paths>({
      fetch: cachingFetch, // 認証系 POST のクライアント側キャッシュ (Documents/Plan/AuthCache)
      headers: {
        "cookie": cookie,
        // RFC 6749 OAuth2 における Request は x-www-form-urlencoded
        "Content-Type": "application/x-www-form-urlencoded",
      },
      bodySerializer: xWwwFormUrlencode,
    });
    const response = await client.POST("/api/v2/Cookie2Token", {
      // redirect: "manual" により EasyAuth (App Service 認証) の 302 を追わず opaqueredirect として検出する。
      // 検証環境では EasyAuth のセッション切れ時に Cookie2Token が
      // 302 (Location: login.microsoftonline.com) を返すため、リダイレクトを追ってしまうと
      // CORS 失敗で原因不明の失敗になる。ここで明示的に検出して EasyAuth 再認証へ誘導する。
      redirect: "manual",
      headers: cacheBustHeaders(cacheBustT),
      body: {
        scope: "openid",
        grant_type: "client_credentials",
      }
    });
    // opaqueredirect (redirect:manual で捕捉した 3xx) は EasyAuth の未認証リダイレクト。
    // 検証環境のみで発生する (本番は EasyAuth 無し)。EasyAuth セッションを張り直して復帰する。
    if (response.response?.type === "opaqueredirect" || response.response?.status === 302) {
      setUserState({
        accessToken: "",
        expiresAt: undefined,
        iat: undefined,
        loginAttemptAt: loginAttemptAt,
        authTokenState: "FailCookie2Token",
        error: "Cookie2Token was redirected by EasyAuth (App Service authentication)",
      }, undefined);
      // EasyAuth 再認証へ誘導する (この後フルページ遷移)。ただし再認証から戻った直後に
      // また 302 = ループの場合、および遷移開始済みの並行呼び出しは no-op となり、
      // 未ログイン (undefined) として通常フロー (/auth/login) に委ねる。
      easyAuthRedirect.handleEasyAuthRedirect();
      return undefined;
    }
    // ここに到達 = 302 以外で決着 (成功 / 非 302 失敗)。復帰直後フラグを解除し、
    // 後のセッション切れ (302) を復帰直後と誤判定しないようにする。
    easyAuthRedirect.endAttempt();
    const data = response.data;
    if (data?.id_token && data?.access_token) {
      const accessToken: string = data.access_token;
      // この /api/v2/UserInfo 呼び出しはトークンの検証機能を兼ねているため setStateAuthToken よりも前に実施する
      const responseUserInfo = await client.POST("/api/v2/UserInfo", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          ...cacheBustHeaders(cacheBustT),
        }
      });
      // ページから /api/v2/UserInfo を呼ぶ場合については src/composables/view-model/user.ts に setStateUserInfo を記述している
      const userInfoItem = responseUserInfo?.data;
      // UserInfo の取得はトークンの検証を兼ねている。取得できなければ未ログインとして扱う
      // (これにより各ページの checkPermission が undefined を受け取り /auth/login へ遷移する)
      if (!userInfoItem) {
        setUserState({
          accessToken: "",
          expiresAt: undefined,
          iat: undefined,
          loginAttemptAt: loginAttemptAt, // ログインを試みたことは記録したい
          authTokenState: "FailCookie2Token",
          error: "failed to fetch UserInfo",
        }, undefined);
        return undefined;
      }
      const jwt = jwtDecode<JwtPayload & { entitlements?: string }>(data.id_token);
      setUserState({
        ...jwt,
        accessToken: accessToken,
        expiresAt: jwt.exp ?? undefined,
        iat: jwt.iat ?? undefined,
        entitlements: jwt.entitlements ?? undefined,
        loginAttemptAt: loginAttemptAt,
        authTokenState: "Authenticated",
      }, userInfoItem);
      return accessToken;
    }
    setUserState({
      accessToken: "",
      expiresAt: undefined,
      iat: undefined,
      loginAttemptAt: loginAttemptAt, // ログインを試みたことは記録したい
      authTokenState: "FailCookie2Token",
    }, undefined);
    return undefined;
  };
  const fetchOption: AccessTokenWithLoginOption = { getAccessTokenWithLogin };
  // ここまで トークン取得用コード

  const getUserInfo = async (cacheBustT?: number): Promise<UserInfo> => {
    const client = await cWebCatalogApi.createOpenapiFetch(fetchOption, { cacheBustT });
    const response = await client?.POST("/api/v2/UserInfo", { headers: cacheBustHeaders(cacheBustT) });
    const userInfo = convertToUserInfo(response?.data);
    // 自動的なアクセストークン取得に伴い cWebCatalogApi.ts でも /api/v2/UserInfo アクセス後 setUserState を呼び出す
    await state.setUserInfo(userInfo);
    return userInfo;
  };

  /**
   * id_token も含めて 明示的に User 情報更新する
   * @param options.t 強制再取得用の時刻 (ミリ秒。Date.now())。指定時はクライアント側キャッシュを無効化して取り直す。
   *   呼び出し側 (/pay 等) が呼び出しタイミングで決定して渡す (Documents/Plan/AuthCache)。
   */
  const updateUserInfo = async (options: UpdateUserInfoOptions = {}) => {
    // AuthToken を取得しなおす。accessToken にひもづいた権限管理をバックエンドが行っているため
    clearUserState();
    await getUserInfo(options.t);
  };

  const changeUserSettings = async (userSettings: {
    nickName: string
    birthDate: string /* ISO8601 日付 yyyy-MM-dd 形式 */
    displayR18: boolean
  }) => {
    const client = await cWebCatalogApi.createOpenapiFetch(fetchOption);
    if (!client) {
      return;
    }
    // これらを並列実行する動作速度上の理由はない
    await client.POST("/api/v2/UpdateBirthDate", {
      body: {
        birthDate: userSettings.birthDate
      }
    });
    await client.POST("/api/v2/UpdateDisplayR18", {
      body: {
        displayR18: userSettings.displayR18
      }
    });
    // ニックネーム設定はリスクが高く、挙動上の変化をしないため最後にする
    await client.POST("/api/v2/UpdateNickname", {
      body: {
        nickname: userSettings.nickName
      }
    });


    // 更新直後はダイアログ open 時などに取った UserInfo が 10 秒キャッシュに残るため
    // cache-bust 付きで強制再取得する (Documents/Plan/AuthCache)。
    await updateUserInfo({ t: Date.now() }); // 値を再取得して state に保存する
  };
  
const updatePushSettings = async (enabled: boolean, activity: PushActivity) => {
    // プッシュ通知の設定を更新する
    pushState.set({
      push: {
        enabled: enabled,
        activity: enabled ? activity : "none"
      },
      deviceId: pushState.get().value.deviceId
    });
    // プッシュ通知のFID取得は registerPushSubscription に任せる
};

  const registerPushSubscription = async (messaging: Messaging | undefined = undefined): Promise<boolean> => {
    const fid = await getFid(messaging);
    if (!fid) {
      return false;
    }
    const client = await cWebCatalogApi.createOpenapiFetch(fetchOption);
    if (!client) {
      return false;
    }

    const pState = pushState.get().value;
    let deviceId = pState.deviceId;
    if(!deviceId) {
      deviceId = crypto.randomUUID();
      pushState.set({
        push: pState.push,
        deviceId: deviceId,
      });
    }
    const response = await client.POST("/api/v2/RegistFid", {
      body: {
        fid: fid,
        deviceid: deviceId,
        status: pState.push.activity,
      }
    });

    // サーバからデバイスIdが更新されて帰ってきたらstateを更新する。
    if(response.data?.success && response.data?.deviceid != deviceId) {
      pState.deviceId = response.data?.deviceid;
      pushState.set(pState);
    }

    return response.data?.success ?? false;
  };

  const unregisterPushSubscription = async (messaging: Messaging | undefined = undefined): Promise<boolean> => {
  // Firebase Installation の破棄を行う
  if (messaging) {
    try {
      await deleteInstallations(getInstallations(messaging.app));
    }
    catch {
      return false;
    }
  }
  // サーバ側のFID登録情報の削除
  const client = await cWebCatalogApi.createOpenapiFetch(fetchOption);
  if (!client) {
    return false;
  }
  const response = await client.POST("/api/v2/RemoveFid", {
    body: {
      deviceid: pushState.get().value.deviceId || ""
    }
  });
  return response.data?.success ?? false;
};
  const getFid = async (fcmMessageing: Messaging | undefined = undefined) => {
    if (!isPush() || !fcmMessageing) {
      return undefined;
    }
    const fid = await getId(getInstallations(fcmMessageing.app));
    return fid;
  };

  const promotePaymentDialog = usePromotePaymentDialogState();
  const checkPermission = async (viewmode: MainViewMode) => {
    if (!isLoginAttempted()) {
      // console.log("checkPermission updateUserInfo start. token:", state.get().value.token);
      await updateUserInfo();
      // console.log("checkPermission updateUserInfo end. token:", state.get().value.token);
    }
    // EasyAuth 再認証のためフルページ遷移中は、解決しない Promise を返して
    // 呼び出し側の navigateTo("LOGIN") 等の SPA 遷移を実行させない (競合防止)。
    // まもなくページがアンロードされるためこの Promise が解決される必要はない。
    // メイン導線は本ガードで保護済み。副次経路 (pay/process 等) は未ガードだが、
    // 検証環境限定かつフルページ遷移でアンロードされるため許容する。
    // 認証後は post_login_redirect_uri 経由のフルリロードで復帰する。
    if (easyAuthRedirect.isNavigatingAway()) {
      return new Promise<never>(() => {});
    }
    if (!isLogin()) {
      // ログイン試行後 未ログインの場合は undefined を返す
      promotePaymentDialog.value = "noLogin";
      return undefined;
    }
    if (isAuthorizedRole(viewmode, userInfo().roles)) {
      // ログイン済みで 無制限に利用できる権限がある (Staff(準備会)/Gold 系。権限表〇)
      promotePaymentDialog.value = "authorized";
      return true;
    }
    // △(サークル閲覧系)ページ: Bronze/Silver は3分時限閲覧 (時限閲覧=12 保持中) のみ利用可
    // (2026-07-06 権限再編。Documents/Plan/Trial3minutes/Plan20260706.md §6)
    if (isTimeboxedMode(viewmode) && trialActive.value) {
      promotePaymentDialog.value = "authorized";
      return true;
    }
    // ログイン済みで 権限がない (時限ブロック中 / 出力系 等)。
    // 遷移 (課金ページへのリダイレクト等) は各ページの false 分岐が担う
    // (△ページは getPaymentRouteWithFrom で遷移元 from を付けて /pay へ。Plan20260706 §6)
    promotePaymentDialog.value = "showPromote";
    return false;
  };

  const getUserSnsLogins: () => Promise<IUserSnsAuth[] | undefined> = async () => {
    const client = await cWebCatalogApi.createOpenapiFetch(fetchOption);
    const response = await client?.GET("/api/v2/UserSnsLogins");
    return response?.data?.userSnsAuths;
  };

  /**
   * 決済開始リンクを表示するか WebCatalog一般側 (国際版ではない) では Gold
   */
  const canStartPayment = () => userInfo().roles.indexOf("Gold") < 0;

  /**
   * 表示上の会員区分が Silver か (Documents/Plan/SilverOverview.md / ADR-007)
   * Silver は閲覧可だが持ち出し不可。
   * ※ お気に入り表示の 10 件制限は 2026-07 権限再編 (PR 9121) で撤廃済み。
   *   現在 UI 側の呼び出し元は無いが、会員区分判定用に残置している。
   */
  const isSilver = () => userInfo().roles.includes("Silver");

  /**
   * 持ち出し系 (印刷/エクスポート/pixiv/CSV) を利用できるか = Gold 相当 (Platinum/Gold/FreeRenewal)。
   * 持ち出し系 viewMode の権限表 (urlHelper) を流用して判定する。Silver/Bronze は false。
   */
  const canUseOutputFeature = () => isAuthorizedRole("PIXIV_EXPORT", userInfo().roles);

  // ===== 3分間 時限閲覧 (2026-07-06 権限再編。Documents/Plan/Trial3minutes/Plan20260706.md) =====
  // 旧 effectiveRoles (1ランク上乗せ) 方式は廃止。△(サークル閲覧系)モードは
  // 「無制限ロール (Staff(準備会)/Gold 系) or ウィンドウ内 (trialActive)」で判定する。
  // ※ お気に入り件数制限 (10件上限) は再編で撤廃済み (PR 9121)。
  const { trialActive } = useTrialUplift();
  // △モードを現時点で利用できるか。メニュー導線 (ヘッダのマップ導線 等) の表示判定に使う。
  // 最終判定はサーバー (AuthorizeI18NOrRenewal) が担う。
  const isViewableMode = (mode: MainViewMode) =>
    isAuthorizedRole(mode, userInfo().roles) || (isTimeboxedMode(mode) && trialActive.value);

  return {
    /**
     * 取得済みのユーザー情報
     */
    userInfo,
    /**
     * サーバーにアクセスしてユーザー情報を再取得する
     */
    getUserInfo,
    /**
     * サーバーにアクセスしてユーザー情報を再取得する
     */
    updateUserInfo,
    /**
     * 権限チェック
     * この画面の呼び出しタイミングでログインを試みていなければログイン試行をする
     * @param mode 表示ViewMode
     * @returns true = 権限有り, false = 権限無し(ログイン済み), undefined = 未ログイン
     */
    checkPermission,
    /**
     * ユーザー情報を更新
     */
    changeUserSettings,
    /**
     * 通知設定を更新
     */
    updatePushSettings,
    /**
     * 表示可能なRoleのうちの1つを返す
     */
    displayStatus,
    /**
     * ログインを試みていれば true
     */
    isLoginAttempted,
    /**
     * ログイン状態
     * @returns トークンが有効でなくても 直近有効なログインをしていれば True
     */
    isLogin,
    /**
     * 認可: サービスを使用できるステータスか
     */
    isAuthorization,
    /**
     * 成人向けコンテンツを表示できるか
     * circlems_r18: 1 かつ 誕生日換算で18歳以上
     */
    canDisplayR18,
    /**
     * 課金前ユーザー情報入力が必要か（生年月日。成人コンテンツ表示フラグは問わない）
     */
    needsUserInfoForPayment,
    /**
     * 決済開始できるか を制御する
     */
    canStartPayment,
    /**
     * 表示上の会員区分が Silver か (生の会員資格。据え置き)
     */
    isSilver,
    /**
     * 持ち出し系を利用できるか (Gold 相当)
     */
    canUseOutputFeature,
    // 3分時限閲覧 (Documents/Plan/Trial3minutes/Plan20260706.md): △モードは 無制限ロール or trialActive
    isViewableMode,
    clearUserState,
    setUserState,
    getAuthToken,
    getUserSnsLogins,
    /**
     * state に保存されている通知設定を返す
     */
    getPushState,
    /**
     * プッシュ通知を利用するか
     */
    isPush,
    /**
     * プッシュ通知の登録、トークンの更新
     */
    registerPushSubscription,
    /**
     * プッシュ通知の登録トークンの削除
     */
    unregisterPushSubscription,
    /**
     * WebAPIアクセスのためのトークンを取得する非同期関数
     */
    getAccessTokenWithLogin,
  };
};

export const getAuth2LogOutUrl = () => {
  const config = useCirclemsConfig().get();
  return `${config.circlemsConfig.CIRCLE_MS_AUTH2_BASE_URL}/account/logOutChild?returnUrl=${location.origin}`;
};

export const getAuth2LoginUrls = (returnUrl: string) => {
  const config = useCirclemsConfig().get();
  const encodedReturnUrl = encodeURIComponent(returnUrl);
  return {
    circlems: `${config.circlemsConfig.CIRCLE_MS_AUTH2_BASE_URL}?returnUrl=${encodedReturnUrl}`,
    google: `${config.circlemsConfig.CIRCLE_MS_AUTH2_BASE_URL}/account/externalLogIn?provider=https://accounts.google.com&returnUrl=${encodedReturnUrl}`
  };
};

/**
 * "/api/v2/UserInfo" の結果から undefined/null を含まないオブジェクトに変換する
 * 未ログイン状態で利用する nickname "Anonymous" をここで指定している
 */
const convertToUserInfo = (data: components["schemas"]["V2UserInfoItem"] | undefined): UserInfo => (
  {
    sub: data?.sub ?? "",
    nickname: data?.nickname ?? "Anonymous",
    email: data?.email ?? "",
    email_verified: data?.email_verified ?? false,
    updated_at: data?.updated_at ?? 0,
    circlems_r18: data?.circlems_r18 ?? 0,
    can_show_circle_menu: data?.can_show_circle_menu ?? false,
    birthdate: data?.birthdate ? data?.birthdate : undefined,
    // この時点で有効なRoleのみに絞り込んでからStateに格納する
    roles: (<Role[]>data?.roles ?? []).filter(role => validRoleList.includes(role)),
  }
);

export const getAddExternalLoginUrl = (sns: IUserSnsAuth) => {
  if (sns.loginProvider) {
    const config = useCirclemsConfig().get();
    return `${config.circlemsConfig.CIRCLE_MS_AUTH2_BASE_URL}/Account/AddExternalLogin?provider=${sns.loginProvider}&returnUrl=${location.origin}`;
  }
  return undefined;
};

export const getRemoveExternalLoginUrl = (sns: IUserSnsAuth) => {
  if (sns.loginProvider) {
    const config = useCirclemsConfig().get();
    return `${config.circlemsConfig.CIRCLE_MS_AUTH2_BASE_URL}/Account/RemoveExternalLogin?provider=${sns.loginProvider}&returnUrl=${location.origin}`;
  }
  return undefined;
};
